// src/components/CreateCampaignModal.tsx - COMPLETE FIXED VERSION (v2)
import React, { useState, useEffect } from 'react';
import { X, Users, Target, Calendar, Send, MessageSquare, AlertCircle, CheckCircle } from 'lucide-react';
import toast from 'react-hot-toast';
import { apiService, getAspirantId, createCampaignDirect, testCampaignEndpoint, API_BASE_URL } from '../services/api';

interface CreateCampaignModalProps {
  onClose: () => void;
  onSuccess?: () => void;
}

interface CampaignFormData {
  campaignName: string;
  type: 'MANIFESTO' | 'STRATEGY' | 'UPDATE' | 'EVENT' | 'ACHIEVEMENT' | 'SURVEY' | 'RESPONSE';
  targetWardIds: number[];
  scheduledAt: string;
  summary: string;
  content: string;
  documentUrl?: string;
  videoUrl?: string;
  imageUrl?: string;
  feedbackLink?: string;
  language: 'ENGLISH' | 'SWAHILI';
  sendSms: boolean;
}

interface AspirantData {
  id: number;
  name: string;
  wardId?: number;
  wardName?: string;
  constituencyId?: number;
  constituencyName?: string;
}

interface WardDTO {
  id: number;
  name: string;
  code: string;
  population: number;
  constituencyName?: string;
}

export const CreateCampaignModal: React.FC<CreateCampaignModalProps> = ({ onClose, onSuccess }) => {
  const [formData, setFormData] = useState<CampaignFormData>({
    campaignName: '',
    type: 'MANIFESTO',
    targetWardIds: [],
    scheduledAt: '',
    summary: '',
    content: '',
    documentUrl: '',
    videoUrl: '',
    imageUrl: '',
    feedbackLink: '',
    language: 'ENGLISH',
    sendSms: false,
  });
  
  const [aspirantId, setAspirantId] = useState<number | null>(null);
  const [aspirant, setAspirant] = useState<AspirantData | null>(null);
  const [wards, setWards] = useState<WardDTO[]>([]);
  const [selectedWards, setSelectedWards] = useState<number[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [citizenCount, setCitizenCount] = useState<number>(0);
  const [estimatedCost, setEstimatedCost] = useState<number>(0);
  const [apiTested, setApiTested] = useState(false);
  const [apiWorking, setApiWorking] = useState(true);

  // Initialize aspirant ID and test API
  useEffect(() => {
    const id = getAspirantId();
    setAspirantId(id);
    
    if (id) {
      fetchAspirantAndWard(id);
    }
    
    // Test API connectivity
    testApiConnection();
  }, []);

  // Test API connection
  const testApiConnection = async () => {
    try {
      const isWorking = await testCampaignEndpoint();
      setApiWorking(isWorking);
      setApiTested(true);
      
      if (!isWorking) {
        console.warn('⚠️ Campaign API test endpoint not working, but continuing anyway');
      }
    } catch (error) {
      console.error('API test failed:', error);
      setApiWorking(false);
      setApiTested(true);
    }
  };

  // Calculate estimated cost when citizen count changes
  useEffect(() => {
    if (formData.sendSms && citizenCount > 0) {
      // Assuming KES 0.65 per SMS
      const cost = citizenCount * 0.65;
      setEstimatedCost(cost);
    } else {
      setEstimatedCost(0);
    }
  }, [formData.sendSms, citizenCount]);

  // Fetch aspirant info and ward data
  const fetchAspirantAndWard = async (id: number) => {
    try {
      setIsLoading(true);
      
      // Fetch aspirant with ward info
      const aspirantResponse = await apiService.getAspirantWithWard(id.toString());
      
      if (aspirantResponse.data.success) {
        const aspirantData = aspirantResponse.data.data;
        setAspirant(aspirantData);
        
        // If aspirant has wardId, set it as default
        if (aspirantData.wardId) {
          setSelectedWards([aspirantData.wardId]);
          setFormData(prev => ({
            ...prev,
            targetWardIds: [aspirantData.wardId]
          }));
          
          // Try to get citizen count, but don't fail if endpoint doesn't work
          try {
            const count = await apiService.getWardCitizenCount(aspirantData.wardId);
            setCitizenCount(count);
          } catch (countError) {
            console.warn('Could not fetch citizen count, using default:', countError);
            setCitizenCount(1500); // Default fallback value
          }
        }
      }
      
      // Fetch all wards for selection
      try {
        const wardsResponse = await apiService.getWards();
        if (wardsResponse.data.success) {
          setWards(wardsResponse.data.data || []);
        }
      } catch (wardError) {
        console.warn('Could not fetch wards:', wardError);
      }
      
    } catch (error) {
      console.error('Error fetching aspirant and ward:', error);
      toast.error('Failed to load aspirant information');
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    
    if (type === 'checkbox') {
      const checked = (e.target as HTMLInputElement).checked;
      setFormData(prev => ({ ...prev, [name]: checked }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleWardSelection = (wardId: number) => {
    const newSelectedWards = selectedWards.includes(wardId)
      ? selectedWards.filter(id => id !== wardId)
      : [...selectedWards, wardId];
    
    setSelectedWards(newSelectedWards);
    setFormData(prev => ({ ...prev, targetWardIds: newSelectedWards }));
    
    // Update citizen count based on selected wards
    if (newSelectedWards.length > 0) {
      // For now, use a default calculation
      setCitizenCount(newSelectedWards.length * 1500); // Average 1500 citizens per ward
    } else {
      setCitizenCount(0);
    }
  };

  const validateForm = (): boolean => {
    if (!formData.campaignName.trim()) {
      toast.error('Campaign name is required');
      return false;
    }
    
    if (!formData.summary.trim()) {
      toast.error('Campaign summary is required');
      return false;
    }
    
    if (!formData.content.trim()) {
      toast.error('Campaign content is required');
      return false;
    }
    
    if (selectedWards.length === 0) {
      toast.error('Please select at least one ward');
      return false;
    }
    
    if (formData.sendSms && citizenCount === 0) {
      toast.error('Cannot send SMS: No citizens found in selected wards');
      return false;
    }
    
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    if (!aspirantId) {
      toast.error('No aspirant ID found. Please login again.');
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      console.log('📤 Submitting campaign with aspirant ID:', aspirantId);
      
      // ✅ FIXED: Do NOT include aspirantId in the request body
      // The backend gets it from the URL path, not the body
      // ✅ FIXED: Removed smsMessage field - it doesn't exist in backend
      const campaignData = {
        campaignName: formData.campaignName,
        description: formData.summary, // Map summary to description
        type: formData.type,
        content: formData.content,
        summary: formData.summary,
        documentUrl: formData.documentUrl || null,
        videoUrl: formData.videoUrl || null,
        imageUrl: formData.imageUrl || null,
        feedbackLink: formData.feedbackLink || null,
        targetWardIds: selectedWards,
        targetCategories: [], // Add if you have categories
        scheduledAt: formData.scheduledAt || null,
        sendSms: formData.sendSms
        // ✅ REMOVED: smsMessage - this field does not exist in the Campaign entity
      };
      
      console.log('📦 Campaign payload (no aspirantId, no smsMessage):', campaignData);
      
      // Use direct fetch approach for maximum reliability
      const url = `${API_BASE_URL}/api/v1/campaigns/aspirant/${aspirantId}`;
      console.log('📡 Sending to URL:', url);
      
      const token = localStorage.getItem('token');
      
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json; charset=utf-8'
        },
        body: JSON.stringify(campaignData)
      });

      console.log('📥 Response status:', response.status);
      
      if (!response.ok) {
        if (response.status === 401) {
          toast.error('Session expired. Please login again.');
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          localStorage.removeItem('aspirant');
          setTimeout(() => window.location.href = '/login', 2000);
          return;
        }
        
        const errorText = await response.text();
        console.error('❌ Error response:', errorText);
        throw new Error(`HTTP ${response.status}: ${errorText}`);
      }
      
      const responseData = await response.json();
      console.log('✅ Success response:', responseData);
      
      if (responseData.success) {
        toast.success('Campaign created successfully!');
        
        // Trigger event to refresh campaign list
        window.dispatchEvent(new CustomEvent('campaign-created', { 
          detail: { campaignId: responseData.data?.id } 
        }));
        
        if (onSuccess) onSuccess();
        onClose();
      } else {
        throw new Error(responseData.error || 'Failed to create campaign');
      }
      
    } catch (error: any) {
      console.error('❌ Campaign creation error:', error);
      
      let errorMessage = 'Failed to create campaign';
      
      if (error.message) {
        errorMessage = error.message;
      }
      
      // Check for specific error patterns
      if (errorMessage.includes('401') || errorMessage.includes('Session expired')) {
        errorMessage = 'Session expired. Please login again.';
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        localStorage.removeItem('aspirant');
        setTimeout(() => {
          window.location.href = '/login';
        }, 2000);
      }
      
      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const campaignTypes = [
    { value: 'MANIFESTO', label: 'Manifesto', description: 'Share your political agenda' },
    { value: 'STRATEGY', label: 'Strategy', description: 'Campaign strategy updates' },
    { value: 'UPDATE', label: 'Update', description: 'General campaign updates' },
    { value: 'EVENT', label: 'Event', description: 'Announce campaign events' },
    { value: 'ACHIEVEMENT', label: 'Achievement', description: 'Share accomplishments' },
    { value: 'SURVEY', label: 'Survey', description: 'Collect feedback from citizens' },
    { value: 'RESPONSE', label: 'Response', description: 'Respond to citizen concerns' }
  ];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between z-10">
          <div>
            <h2 className="text-xl font-bold text-gray-900">Create New Campaign</h2>
            <p className="text-gray-600 text-sm">Reach citizens with your message</p>
            {aspirantId && (
              <p className="text-xs text-gray-500 mt-1">Aspirant ID: {aspirantId}</p>
            )}
            {apiTested && !apiWorking && (
              <p className="text-xs text-yellow-600 mt-1">
                ⚠️ API test endpoint not responding, but continuing...
              </p>
            )}
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Aspirant Info */}
          {aspirant && (
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
              <div className="flex items-center">
                <Target className="text-blue-600 mr-2" size={18} />
                <span className="font-medium text-blue-900">Aspirant:</span>
                <span className="ml-2 text-blue-800">{aspirant.name}</span>
              </div>
              {aspirant.wardName && (
                <div className="mt-2 text-sm text-blue-700">
                  <span className="font-medium">Ward:</span> {aspirant.wardName}
                  {aspirant.wardId && ` (ID: ${aspirant.wardId})`}
                </div>
              )}
              <div className="mt-2 text-sm text-blue-700">
                <span className="font-medium">Estimated Reach:</span> {citizenCount.toLocaleString()} citizens
              </div>
            </div>
          )}

          {/* Campaign Details */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Left Column */}
            <div className="space-y-6">
              {/* Campaign Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Campaign Name *
                </label>
                <input
                  type="text"
                  name="campaignName"
                  value={formData.campaignName}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="e.g., Education Improvement Plan"
                  required
                  disabled={isLoading}
                />
              </div>

              {/* Campaign Type */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Campaign Type *
                </label>
                <select
                  name="type"
                  value={formData.type}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  disabled={isLoading}
                >
                  {campaignTypes.map(type => (
                    <option key={type.value} value={type.value}>
                      {type.label}
                    </option>
                  ))}
                </select>
                {campaignTypes.find(t => t.value === formData.type)?.description && (
                  <p className="mt-1 text-sm text-gray-500">
                    {campaignTypes.find(t => t.value === formData.type)?.description}
                  </p>
                )}
              </div>

              {/* Target Wards */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Target Wards *
                </label>
                <div className="border border-gray-300 rounded-lg p-3 max-h-48 overflow-y-auto">
                  {isLoading ? (
                    <div className="text-center py-4">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600 mx-auto"></div>
                      <p className="mt-2 text-sm text-gray-500">Loading wards...</p>
                    </div>
                  ) : wards.length > 0 ? (
                    <div className="space-y-2">
                      {wards.map(ward => (
                        <label
                          key={ward.id}
                          className={`flex items-center p-2 rounded-lg cursor-pointer transition-colors ${
                            selectedWards.includes(ward.id)
                              ? 'bg-primary-50 border border-primary-200'
                              : 'hover:bg-gray-50'
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={selectedWards.includes(ward.id)}
                            onChange={() => handleWardSelection(ward.id)}
                            className="h-4 w-4 text-primary-600 rounded focus:ring-primary-500"
                            disabled={isLoading}
                          />
                          <div className="ml-3">
                            <div className="font-medium text-gray-900">{ward.name}</div>
                            <div className="text-xs text-gray-500">
                              {ward.constituencyName} • {ward.population?.toLocaleString() || 'N/A'} people
                            </div>
                          </div>
                        </label>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-4 text-gray-500">
                      No wards available
                    </div>
                  )}
                </div>
                <div className="mt-2 flex items-center text-sm">
                  <Users size={16} className="text-gray-400 mr-1" />
                  <span className="text-gray-600">
                    {selectedWards.length} ward(s) selected • {citizenCount.toLocaleString()} estimated reach
                  </span>
                </div>
              </div>

              {/* Schedule */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Schedule (Optional)
                </label>
                <div className="flex items-center">
                  <Calendar size={18} className="text-gray-400 mr-2" />
                  <input
                    type="datetime-local"
                    name="scheduledAt"
                    value={formData.scheduledAt}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    disabled={isLoading}
                  />
                </div>
                <p className="mt-1 text-sm text-gray-500">
                  Leave empty to publish immediately
                </p>
              </div>
            </div>

            {/* Right Column */}
            <div className="space-y-6">
              {/* Language */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Language *
                </label>
                <div className="flex space-x-4">
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="language"
                      value="ENGLISH"
                      checked={formData.language === 'ENGLISH'}
                      onChange={handleInputChange}
                      className="h-4 w-4 text-primary-600"
                      disabled={isLoading}
                    />
                    <span className="ml-2 text-gray-700">English</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="language"
                      value="SWAHILI"
                      checked={formData.language === 'SWAHILI'}
                      onChange={handleInputChange}
                      className="h-4 w-4 text-primary-600"
                      disabled={isLoading}
                    />
                    <span className="ml-2 text-gray-700">Swahili</span>
                  </label>
                </div>
              </div>

              {/* SMS Option */}
              <div className="border border-gray-200 rounded-xl p-4">
                <label className="flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    name="sendSms"
                    checked={formData.sendSms}
                    onChange={handleInputChange}
                    className="h-5 w-5 text-primary-600 rounded focus:ring-primary-500"
                    disabled={isLoading || citizenCount === 0}
                  />
                  <div className="ml-3">
                    <div className="font-medium text-gray-900">Send SMS to Citizens</div>
                    <div className="text-sm text-gray-600 mt-1">
                      Deliver campaign message via SMS to all citizens in selected wards
                    </div>
                  </div>
                </label>
                
                {formData.sendSms && citizenCount > 0 && (
                  <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg">
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="flex items-center text-green-700">
                          <Send size={16} className="mr-2" />
                          <span className="font-medium">SMS Cost Estimate</span>
                        </div>
                        <div className="text-sm text-green-600 mt-1">
                          {citizenCount.toLocaleString()} citizens × KES 0.65 per SMS
                        </div>
                      </div>
                      <div className="text-lg font-bold text-green-800">
                        KES {estimatedCost.toLocaleString('en-KE', { minimumFractionDigits: 2 })}
                      </div>
                    </div>
                  </div>
                )}
                
                {formData.sendSms && citizenCount === 0 && (
                  <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                    <div className="flex items-center text-yellow-700">
                      <AlertCircle size={16} className="mr-2" />
                      <span className="text-sm">No citizens found in selected wards</span>
                    </div>
                  </div>
                )}
              </div>

              {/* Summary */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Campaign Summary *
                </label>
                <textarea
                  name="summary"
                  value={formData.summary}
                  onChange={handleInputChange}
                  rows={3}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="Brief description of your campaign..."
                  required
                  disabled={isLoading}
                />
                <p className="mt-1 text-sm text-gray-500">
                  This will be used for SMS messages (max 160 characters)
                </p>
              </div>

              {/* Links Section */}
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Document URL (Optional)
                  </label>
                  <input
                    type="url"
                    name="documentUrl"
                    value={formData.documentUrl}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder="https://example.com/manifesto.pdf"
                    disabled={isLoading}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Feedback Link (Optional)
                  </label>
                  <input
                    type="url"
                    name="feedbackLink"
                    value={formData.feedbackLink}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder="https://forms.google.com/feedback"
                    disabled={isLoading}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Campaign Content */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Campaign Content *
            </label>
            <textarea
              name="content"
              value={formData.content}
              onChange={handleInputChange}
              rows={6}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              placeholder="Write your full campaign message here..."
              required
              disabled={isLoading}
            />
            <div className="mt-2 flex items-center justify-between text-sm text-gray-500">
              <div className="flex items-center">
                <MessageSquare size={14} className="mr-1" />
                <span>Detailed campaign message</span>
              </div>
              <span>{formData.content.length} characters</span>
            </div>
          </div>

          {/* Cost Summary */}
          {(formData.sendSms && estimatedCost > 0) && (
            <div className="bg-gray-50 border border-gray-200 rounded-xl p-4">
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="font-medium text-gray-900">Campaign Cost Summary</h4>
                  <div className="text-sm text-gray-600 mt-1">
                    SMS delivery to {citizenCount.toLocaleString()} citizens
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-2xl font-bold text-gray-900">
                    KES {estimatedCost.toLocaleString('en-KE', { minimumFractionDigits: 2 })}
                  </div>
                  <div className="text-sm text-gray-500">Estimated cost</div>
                </div>
              </div>
            </div>
          )}

          {/* Form Actions */}
          <div className="flex items-center justify-between pt-6 border-t">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
              disabled={isSubmitting}
            >
              Cancel
            </button>
            
            <div className="flex items-center space-x-4">
              {formData.sendSms && estimatedCost > 0 && (
                <div className="text-sm text-gray-600">
                  <span className="font-medium">Cost:</span> KES {estimatedCost.toLocaleString('en-KE', { minimumFractionDigits: 2 })}
                </div>
              )}
              
              <button
                type="submit"
                disabled={isSubmitting || isLoading}
                className="px-6 py-2 bg-primary-600 text-white rounded-lg font-medium hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
              >
                {isSubmitting ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Creating...
                  </>
                ) : (
                  <>
                    <CheckCircle size={18} className="mr-2" />
                    Create Campaign
                  </>
                )}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateCampaignModal;
